import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { ArrowUpRight, Building2, ChartNoAxesColumn } from "lucide-react";
import Panel from "@ui/panel/Panel";
import { useRouter } from "src/services/Router";
import { describeTrafficChange, formatAnomaliesDate, getTrafficChangeStatus, getTrafficProblems, getTrafficComparisonPeriod, type OverviewAnomalies } from "./overviewAnomalies";
import { useTranslation } from "react-i18next";
import { getAppLanguage } from "src/i18n";
import { issueLevels } from "./overviewIssuePresentation";
import { AnalysisRow } from "./OverviewAnalysisRows";

const Root = styled.div`
	display: flex; flex-direction: column; min-width: 0; color: #344054; font-size: 14px;
	container-type: inline-size;
	.updated, .empty { margin: 0; padding: 10px 20px; color: #667085; font-size: 13px; line-height: 1.5; }
	.updated { border-top: 1px solid #edf0f5; background: transparent; }
	.empty { color: #526174; background: #fafbfc; }
`;
const Footer = styled.div`
	display: flex; justify-content: flex-end; padding-top: 12px; margin-top: 12px;
	border-top: 1px solid #e1e6ef; flex-shrink: 0;
	button { min-height: 38px; padding: 8px 18px; border: 1px solid #cfd6e0; border-radius: 6px; background: #fff; color: #344054; font: inherit; font-size: 14px; cursor: pointer; }
	button:hover { background: #f5f7fa; }
	button:focus-visible { outline: 2px solid #6374d9; outline-offset: 2px; }
`;

export function OverviewAnomaliesDetails({ result, onClientNavigate }: { result: OverviewAnomalies; onClientNavigate?: (clientId: number) => void }) {
	const { t } = useTranslation();
	const language = getAppLanguage();
	const locale = language === "ru" ? "ru-RU" : "en-GB";
	const problems = getTrafficProblems(result.changes).sort((a, b) => Number(getTrafficChangeStatus(b) === "critical") - Number(getTrafficChangeStatus(a) === "critical"));
	const commonPeriod = getTrafficComparisonPeriod(result);
	const periodText = commonPeriod ? t("traffic.comparison", { current: formatAnomaliesDate(commonPeriod.bucket), previous: formatAnomaliesDate(commonPeriod.previousBucket) }) : t("traffic.datesPerClient");
	const dateSummary = [periodText, result.asOf ? t("traffic.dataAsOf", { date: formatAnomaliesDate(result.asOf) }) : null].filter(Boolean).join(" · ");
	return <Root>
		{problems.length === 0 ? <div className="empty">{t("traffic.noProblems")}</div> : problems.map(change => {
			const status = getTrafficChangeStatus(change) === "critical" ? "critical" : "warning";
			const tone = issueLevels[status];
			const barColor = status === "critical" ? "#dc2626" : "#d99a00";
			const percent = change.changePercent;
			const percentText = percent !== null && Number.isFinite(percent) ? `${percent > 0 ? "+" : ""}${percent.toLocaleString(locale, { maximumFractionDigits: 2 })}%` : "—";
			const description = commonPeriod ? t("traffic.comparison", { current: formatAnomaliesDate(commonPeriod.bucket), previous: formatAnomaliesDate(commonPeriod.previousBucket) }) : t("traffic.comparison", { current: formatAnomaliesDate(change.bucket ?? result.bucket), previous: formatAnomaliesDate(change.previousBucket ?? result.previousBucket) });
			const content = <>
				<span className="subject-icon" aria-hidden="true">{change.clientId === undefined ? <ChartNoAxesColumn size={19} /> : <Building2 size={19} />}</span>
				<span className="subject"><span className="subject-title"><strong>{change.label}</strong><span className="severity">{status === "critical" ? t("traffic.sharpChange") : t("traffic.requiresAttention")}</span></span>{!commonPeriod && <span className="description">{description}</span>}</span>
				<span className="metric" aria-label={language === "ru" ? describeTrafficChange(change) : `${t("traffic.change")}: ${percentText}`}><strong>{change.fromZero ? t("traffic.riseFromZero") : percentText}</strong><small>{t("traffic.change")}</small></span>
				<span className="sample" aria-label={`${t("traffic.transactions")}: ${change.previousCount ?? "—"} → ${change.currentCount ?? "—"}`}><strong><span style={{ color: "#667085" }}>{change.previousCount?.toLocaleString(locale) ?? "—"}</span><span style={{ margin: "0 5px", color: "#98a2b3" }} aria-hidden="true">→</span>{change.currentCount?.toLocaleString(locale) ?? "—"}</strong><small>{t("traffic.transactions")}</small></span>
				{change.clientId !== undefined && <ArrowUpRight className="open" size={16} aria-hidden="true" />}
			</>;
			return <AnalysisRow key={change.id} $color={tone.color} $background={tone.background} $barColor={barColor} aria-label={change.label}>{change.clientId !== undefined
				? <a className="issue-row" href={`/clients/${change.clientId}`} onClick={event => {
						if (!onClientNavigate || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
						event.preventDefault();
						onClientNavigate(change.clientId!);
					}} aria-label={`${change.label}. ${language === "ru" ? describeTrafficChange(change) : `${t("traffic.change")}: ${percentText}`}`}>{content}</a>
				: <div className="issue-row">{content}</div>}</AnalysisRow>;
		})}
		<p className="updated">{dateSummary}</p>
	</Root>;
}

export function OverviewAnomaliesPopup({ result, onClose }: { result: OverviewAnomalies; onClose: () => void }) {
	const { t } = useTranslation();
	const popup = useRef<HTMLDivElement>(null);
	const router = useRouter();
	useEffect(() => {
		const previousFocus = document.activeElement;
		popup.current?.focus();
		return () => { if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus(); };
	}, []);
	if (typeof document === "undefined") return null;
	return createPortal(<div ref={popup} role="dialog" aria-modal="true" aria-label={t("traffic.details")} tabIndex={-1}
		onKeyDown={event => {
			if (event.key !== "Tab") return;
			const controls = popup.current?.querySelectorAll<HTMLElement>('button:not(:disabled), [href], [tabindex="0"]');
			const first = controls?.[0];
			const last = controls?.[controls.length - 1];
			if (!first || !last) { event.preventDefault(); return; }
			if (event.shiftKey && (document.activeElement === first || document.activeElement === popup.current)) { event.preventDefault(); last.focus(); }
			else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
		}}>
		<Panel decorator="absolute" label={t("traffic.controlDay")} onClose={onClose} growContent disableScroll
			sx={{ width: "820px", maxWidth: "94vw", maxHeight: "90dvh", overflow: "hidden" }}>
			<div style={{ overflowY: "auto", minHeight: 0, scrollbarGutter: "stable" }} tabIndex={0} aria-label={t("traffic.problems")}>
				<OverviewAnomaliesDetails result={result} onClientNavigate={clientId => { onClose(); router.navigate("client", { cid: String(clientId) }); }} />
			</div>
			<Footer><button type="button" onClick={onClose}>{t("common.close")}</button></Footer>
		</Panel>
	</div>, document.body);
}
